import { useState, useEffect } from "react";
import { useAuth, API } from "../context/AuthContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { PageHeader, StatCard, SectionCard } from "../components/ui/page-shell";
import { LogOut, Trash2, User as UserIcon, Mail, Calendar, Loader2, Bot, Shield } from "lucide-react";
import { toast } from "sonner";

export default function Account() {
  const { user, logout, deleteAccount } = useAuth();
  const [deleting, setDeleting] = useState(false);
  const [chatbotCount, setChatbotCount] = useState(0);

  useEffect(() => {
    const fetchUsage = async () => {
      try {
        const token = sessionStorage.getItem("___at");
        const t = token ? atob(token) : null;
        if (!t) return;
        const { data } = await axios.get(`${API}/workspace/usage`, { headers: { Authorization: `Bearer ${t}` } });
        setChatbotCount(data.chatbots_used || 0);
      } catch {}
    };
    fetchUsage();
  }, []);

  const maxChatbots = user?.max_chatbots ?? 1;
  const limitDisplay = maxChatbots === -1 ? "Unlimited" : maxChatbots;

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out successfully");
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await deleteAccount();
      toast.success("Account deleted");
    } catch (e) {
      toast.error("Failed to delete account");
    } finally { setDeleting(false); }
  };

  return (
    <div className="space-y-6" data-testid="account-page">
      <PageHeader title="Account Settings" subtitle="Manage your profile, plan, and account security." />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Plan Limit" value={limitDisplay} icon={Bot} accent="blue" helper={maxChatbots === -1 ? "unlimited" : `chatbot${maxChatbots === 1 ? "" : "s"} allowed on your plan`} />
        <StatCard label="In Use" value={chatbotCount} icon={Shield} accent="emerald" helper="active chatbots" />
        <StatCard label="Member Since" value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"} icon={Calendar} accent="violet" helper="account created" />
      </div>

      <SectionCard title="Profile" description="Your account identity within Bicarra.">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-blue-600 text-white flex items-center justify-center text-2xl font-extrabold">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div>
            <p className="text-lg font-bold text-slate-900">{user?.name || "User"}</p>
            <p className="text-sm text-slate-500 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {user?.email}</p>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Session" description="Sign out from this device.">
        <Button variant="outline" onClick={handleLogout} data-testid="logout-btn" className="border-slate-200">
          <LogOut className="w-4 h-4 mr-2" /> Log Out
        </Button>
      </SectionCard>

      <SectionCard title="Danger zone" description="Permanently delete your account and all associated data." className="border-rose-200">
        <p className="text-sm text-slate-600 mb-4">Once you delete your account, there is no going back. This will permanently delete your account, chatbots, conversations, and all associated data.</p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="border-rose-300 text-rose-600 hover:bg-rose-50" data-testid="delete-account-trigger">
              <Trash2 className="w-4 h-4 mr-2" /> Delete Account
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Account?</AlertDialogTitle>
              <AlertDialogDescription>This action cannot be undone. This will permanently delete your account, all your chatbots, conversations, and associated data.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteAccount} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-account">
                {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Delete Account
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SectionCard>
    </div>
  );
}
